export type VideoPlaybackOptions = {
  /** Force download + blob URL instead of same-origin /api/github-media stream. */
  skipProxy?: boolean;
  /** Only try HTTPS CDN stream (media.githubusercontent.com), not blob or proxy. */
  streamOnly?: boolean;
  /** Private repo: tokenized raw.githubusercontent.com only (no same-origin proxy). */
  rawStreamOnly?: boolean;
  /** Open-in-new-tab: same-origin /api/github-media (PAT raw 404s in browser). */
  openInNewTab?: boolean;
  /** Skip HTTPS stream attempt (blob/proxy only). */
  skipStream?: boolean;
};

/** True on phones/tablets where blob: MP4 URLs often fail to decode. */
export function prefersVideoStreamPlayback(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
}

export function isFirefoxBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /firefox|fxios/i.test(navigator.userAgent);
}

/** Firefox on public repos: skip proxy. Private repos still use /api/github-media (PAT). */
export function prefersVideoBlobOverProxy(): boolean {
  return isFirefoxBrowser();
}

/** Public repos only: Firefox tries jsDelivr/github raw before blob. */
export function prefersVideoHttpsStreamFirst(): boolean {
  return isFirefoxBrowser() && prefersVideoStreamPlayback();
}

/**
 * Firefox Android rejects cross-origin HTTPS (incl. tokenized raw.githubusercontent.com)
 * in video elements — MediaError 4 src-not-supported. Use same-origin proxy or blob.
 */
export function firefoxRejectsCrossOriginVideoSrc(): boolean {
  return isFirefoxBrowser();
}

/** Firefox: direct video.src for same-origin proxy and blob URLs only. */
export function prefersFirefoxDirectVideoSrc(url: string): boolean {
  return isFirefoxBrowser() && (url.startsWith('/api/') || url.startsWith('blob:'));
}

export function videoPreloadForPlayback(): 'auto' | 'metadata' {
  return isFirefoxBrowser() && prefersVideoStreamPlayback() ? 'auto' : 'metadata';
}
